import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Mic, Video, Trash2 } from 'lucide-react';
import MediaCapture from '../MediaCapture';

/**
 * DailyQuestionModal — Question of the Day modal.
 *
 * Props:
 *   questionData - from useDailyQuestion (has_question, question, day_number, total_questions)
 *   onAnswer     - async (data, mediaFiles) => result — called with { text, title?, year? }
 *   onSkip       - async () => void
 *   onDismiss    - () => void
 */
function DailyQuestionModal({ questionData, onAnswer, onSkip, onDismiss }) {
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [showExtras, setShowExtras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Media capture state
  const [showMediaCapture, setShowMediaCapture] = useState(false);
  const [mediaCaptureMode, setMediaCaptureMode] = useState('audio');
  const [localMediaFiles, setLocalMediaFiles] = useState([]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      localMediaFiles.forEach(media => {
        if (media.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(media.preview);
        }
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const question = questionData?.question;
  if (!question) return null;

  const handleMediaCaptured = (mediaData) => {
    const file = mediaData.file;
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    const maxMB = isVideo ? 500 : isAudio ? 50 : 10;
    if (file.size > maxMB * 1024 * 1024) {
      const label = isVideo ? 'Videos' : isAudio ? 'Audio files' : 'Images';
      alert(`"${file.name}" is too large (${(file.size/(1024*1024)).toFixed(0)} MB). ${label} must be under ${maxMB} MB.`);
      return;
    }
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file,
      preview: mediaData.preview,
      name: file.name,
      size: file.size,
      type: file.type,
      mediaType: mediaData.type,
      duration: mediaData.duration,
      timestamp: mediaData.timestamp,
    };
    setLocalMediaFiles(prev => [...prev, localFile]);
  };

  const removeMedia = (id) => {
    setLocalMediaFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError('Please share your story before submitting.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = { text: text.trim() };
      if (title.trim()) data.title = title.trim();
      if (year) data.year = parseInt(year, 10);

      const mediaFiles = localMediaFiles.map(f => ({ file: f.file, name: f.name }));
      await onAnswer(data, mediaFiles);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await onSkip();
    } catch (err) {
      setSubmitting(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        onClick={onDismiss}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onDismiss}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'white',
              zIndex: 2,
              padding: '4px',
            }}
            aria-label="Close"
          >
            <X size={22} />
          </button>

          {/* Green Header */}
          <div
            style={{
              background: '#556B2F',
              color: 'white',
              padding: '24px 28px 20px',
              borderRadius: '16px 16px 0 0',
            }}
          >
            <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: 600 }}>
              Question of the Day
            </div>
            <div style={{ fontSize: '14px', opacity: 0.85, marginTop: '4px' }}>
              A new question every day
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '24px 28px 28px' }}>
            {/* Chapter badge */}
            <div style={{ marginBottom: '16px' }}>
              <span
                style={{
                  display: 'inline-block',
                  background: '#F0EDE4',
                  color: '#556B2F',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {question.chapter_title}
              </span>
            </div>

            {/* Question text */}
            <div
              style={{
                fontFamily: "'Lora', Georgia, serif",
                fontSize: '22px',
                lineHeight: '1.4',
                color: '#2D2D2D',
                marginBottom: '24px',
              }}
            >
              {question.question_text}
            </div>

            {/* Text area */}
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setError(''); }}
              placeholder="Share your story..."
              rows={5}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid #E8E4D8',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#556B2F'; }}
              onBlur={(e) => { e.target.style.borderColor = '#E8E4D8'; }}
            />

            {/* Record Media buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <button
                onClick={() => { setMediaCaptureMode('audio'); setShowMediaCapture(true); }}
                disabled={submitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: '2px solid #D9D5CC',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  color: '#556B2F',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                <Mic size={16} />
                Record Audio
              </button>
              <button
                onClick={() => { setMediaCaptureMode('video'); setShowMediaCapture(true); }}
                disabled={submitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: '2px solid #D9D5CC',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  color: '#556B2F',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                <Video size={16} />
                Record Video
              </button>
            </div>

            {/* Media preview list */}
            {localMediaFiles.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {localMediaFiles.map((media) => (
                  <div
                    key={media.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      background: '#F8F6F0',
                      borderRadius: '8px',
                      padding: '10px 12px',
                    }}
                  >
                    {media.mediaType === 'video' && media.preview ? (
                      <video
                        src={media.preview}
                        style={{ width: '60px', height: '42px', objectFit: 'cover', borderRadius: '6px', background: '#000' }}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <div
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '50%',
                          background: '#556B2F',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {media.mediaType === 'video' ? (
                          <Video size={18} color="white" />
                        ) : (
                          <Mic size={18} color="white" />
                        )}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', color: '#2D2D2D', fontWeight: 600 }}>
                        {media.mediaType === 'video' ? 'Video' : 'Audio'} recording
                      </div>
                      <div style={{ fontSize: '12px', color: '#8A8A7A' }}>
                        {formatDuration(media.duration)}
                        {media.size ? ` · ${(media.size / (1024 * 1024)).toFixed(1)} MB` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => removeMedia(media.id)}
                      disabled={submitting}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        color: '#C65D34',
                        padding: '4px',
                        flexShrink: 0,
                      }}
                      aria-label="Remove media"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Expandable extras */}
            <button
              onClick={() => setShowExtras(!showExtras)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#8A8A7A',
                fontSize: '14px',
                padding: '8px 0',
                marginTop: '4px',
              }}
            >
              {showExtras ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showExtras ? 'Hide title & year' : '+ Add title & year'}
            </button>

            {showExtras && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '2px solid #E8E4D8',
                    borderRadius: '8px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <input
                  type="number"
                  placeholder="Year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: '100px',
                    padding: '10px 14px',
                    border: '2px solid #E8E4D8',
                    borderRadius: '8px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {/* Error message */}
            {error && (
              <div style={{ color: '#C65D34', fontSize: '14px', marginTop: '8px' }}>
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleSkip}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: 'white',
                  color: '#8A8A7A',
                  border: '2px solid #D9D5CC',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                Skip for Now
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !text.trim()}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: !text.trim() ? '#A8B88C' : '#556B2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: submitting || !text.trim() ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Saving...' : 'Share My Story'}
              </button>
            </div>

            {/* Footer note */}
            <div
              style={{
                textAlign: 'center',
                color: '#B0AEA4',
                fontSize: '13px',
                marginTop: '16px',
              }}
            >
              Shared with your family &middot; Saved in "{question.chapter_title}"
            </div>
          </div>
        </div>
      </div>

      {/* MediaCapture modal (renders on top) */}
      <MediaCapture
        isOpen={showMediaCapture}
        initialMode={mediaCaptureMode}
        onMediaCaptured={handleMediaCaptured}
        onClose={() => setShowMediaCapture(false)}
      />
    </>
  );
}

export default DailyQuestionModal;
